/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './CalendarSourceEvent';

const CalendarSourceEventManager: ComponentManagerFn<'CalendarSourceEvent'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'CalendarSourceEvent'>(
      platform,
      'CalendarSourceEvent',
    ),
    type: 'CalendarSourceEvent',
    name: 'Calendar Source Event',
    isChildAllowed: (page, item, childType) =>
      (['Box'] as ItemType[]).includes(childType),
    defaultValue,
    props: {},
    category: 'Forms',
    icon: { iconName: 'calendar-week' },
    description: `**Overview**<br/>
The Calendar Source Event is a specialized interaction component designed for "Drag-and-Drop Scheduling" workflows.
It represents an "Unscheduled Item" (like a Work Order, a Task, or an Unassigned Shift) that exists outside the calendar but can be dragged onto the Calendar component to create a new event.

**1. Usage Context**<br/>
This component is never used alone. It is part of a specific UI pattern:
* **The Source List:** A list of items (usually a Box List or Collection List) containing tasks that need to be scheduled.
* **The Drop Target:** A Calendar component representing the schedule.
* The Calendar Source Event wraps the items in the source list, making them "Draggable" and compatible with the Calendar's drop logic.

**2. Configuration**<br/>
The component acts primarily as a wrapper and has minimal configuration properties. Its main job is to signal to the platform, "I am a draggable event object."
* **Composition:**
    * **Child Component:** You must place a **Box** inside this component. The Box defines the visual look of the draggable item (e.g., a card with "Task #101 - Repair").
* **Data Context:** Typically used inside a repeater (like BoxList), so each instance inherits the data of a specific record.

**3. Interaction Flow**<br/>
1. **Drag:** The user clicks and drags the CalendarSourceEvent.
2. **Drop:** The user releases it onto a specific day/time slot in the Calendar.
3. **Trigger:** The Calendar fires its **TriggerOnDrop** event.
4. **Logic:** Inside that trigger, you write the logic to update the record (e.g., Update Task Set StartDate = DropDate).`,
  };
};

export default CalendarSourceEventManager;
